import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpenIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { RichText } from "../KaTeXComponent/RichText";

// Like a question body the text can't be selected (a phone's selection menu offers Translate and
// Look Up), and its links don't work (one would leave the exam page).
const textClass =
  "passage-content select-none text-base leading-7 text-slate-800 [-webkit-touch-callout:none] [&_a]:pointer-events-none [&_img]:max-w-full min-[1920px]:text-lg min-[1920px]:leading-8";

// pointer-events stop the mouse; Enter on a link reached with Tab clicks it too.
const blockLinks = (event) => {
  if (event.target.closest?.("a")) event.preventDefault();
};

/**
 * The passage of a run of exam questions (see passageRuns): "Passage for questions 3–5" and the text
 * ({uuid, body} from the attempt's `passages`). In the exam the text scrolls on its own and keeps its
 * place per passage in `scrollPositions` (a Map owned by the page); below xl it stands above the
 * question and can be collapsed. variant="review": a plain card in the attempt review.
 */
function PassagePane({ run, passage, variant = "exam", scrollPositions, className = "" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const bodyId = useId();
  const bodyRef = useRef(null);
  const isReview = variant === "review";
  const title =
    run.start === run.end
      ? t("exam_taking.passage.title_single", { number: run.start + 1 })
      : t("exam_taking.passage.title_range", { from: run.start + 1, to: run.end + 1 });

  // Back where the student left this passage: in another run of it, or after collapsing it.
  useLayoutEffect(() => {
    if (isReview || !open || !bodyRef.current) return;
    bodyRef.current.scrollTop = scrollPositions?.get(run.passageUuid) || 0;
  }, [isReview, open, run.passageUuid, scrollPositions]);

  const heading = (
    <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-indigo-900">
      <BookOpenIcon className="h-5 w-5 flex-shrink-0 text-indigo-600" aria-hidden="true" />
      {title}
    </h2>
  );

  if (isReview) {
    return (
      <section className={`overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm ${className}`}>
        <div className="border-b border-indigo-100 bg-indigo-50/60 px-5 py-3 sm:px-6">{heading}</div>
        <div onClick={blockLinks} className={`${textClass} px-5 py-4 sm:px-6`}>
          <RichText html={passage.body} dollarMath={false} />
        </div>
      </section>
    );
  }

  return (
    <section
      className={`overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm xl:flex xl:max-h-full xl:min-h-0 xl:flex-col xl:self-start ${className}`}
    >
      <div className="flex flex-shrink-0 items-center justify-between gap-3 bg-indigo-50/60 px-4 py-2.5 sm:px-6">
        {heading}
        {/* On a phone only the chevron shows, so the title keeps the row. */}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg bg-white text-sm font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200 transition duration-200 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:w-auto sm:px-3 xl:hidden"
        >
          <span className="sr-only sm:not-sr-only">
            {open ? t("exam_taking.passage.hide") : t("exam_taking.passage.show")}
          </span>
          <ChevronDownIcon
            className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {/* Focusable, so the arrow, page and space keys scroll it; Left and Right still change the question. */}
      <div
        id={bodyId}
        ref={bodyRef}
        role="region"
        tabIndex={0}
        aria-label={title}
        onScroll={(event) => scrollPositions?.set(run.passageUuid, event.currentTarget.scrollTop)}
        onClick={blockLinks}
        className={`${textClass} max-h-[50vh] overflow-y-auto overscroll-contain border-t border-indigo-100 px-4 py-4 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600 sm:px-6 xl:max-h-none xl:min-h-0 ${
          open ? "" : "max-xl:hidden"
        }`}
      >
        <RichText html={passage.body} dollarMath={false} />
      </div>
    </section>
  );
}

export default React.memo(PassagePane);
